import { DeliverySupplierOption } from "../types";
import { getParentSupplierId } from "./resolveDefaultEstimateSplitTransactionType";

function getStrId(val: unknown): string {
  if (val == null || val === "") return "";
  if (typeof val === "object") {
    const asAny = val as { $oid?: string; _id?: unknown };
    if (asAny.$oid) return String(asAny.$oid).trim();
    if (asAny._id != null && asAny._id !== val) return getStrId(asAny._id);
    return "";
  }
  return String(val).trim();
}

function toDeliveryOption(
  supplier: any,
  associateIds: Set<string>,
): DeliverySupplierOption | null {
  const supplierId = getStrId(supplier?._id);
  if (!supplierId) return null;

  const firstBranch = supplier.branches?.[0];
  return {
    supplierId,
    businessName: supplier.businessName || supplier.name || "Unknown Supplier",
    branchName: firstBranch?.branchName ?? supplier.branchName ?? null,
    branchCode: firstBranch?.branchCode ?? supplier.branchCode ?? null,
    isAssociated: associateIds.has(supplierId.toLowerCase()),
    address: supplier.address || supplier.supplierAddress,
  };
}

/**
 * Resolves the family root for a split supplier:
 * parentSupplierId when present, otherwise the supplier itself (parent / standalone).
 */
export function resolveSupplierFamilyRootId(params: {
  splitSupplierId: string;
  orderData?: any;
  allSuppliers?: any[];
}): string {
  const splitId = getStrId(params.splitSupplierId);
  if (!splitId) return "";

  const fromOrder = getStrId(
    getParentSupplierId(splitId, params.orderData),
  );
  if (fromOrder) return fromOrder;

  const splitSupplier = (params.allSuppliers || []).find(
    (s: any) => getStrId(s?._id).toLowerCase() === splitId.toLowerCase(),
  );
  const fromSupplier = getStrId(splitSupplier?.parentSupplierId);
  if (fromSupplier) return fromSupplier;

  return splitId;
}

export type BuildFamilySupplierOptionsParams = {
  splitSupplierId?: string | null;
  splitSupplierName?: string | null;
  orderData?: any;
  allSuppliers?: any[];
  /** Lowercase associate supplier ids. */
  associateIds?: Iterable<string>;
  /** Fallback when family cannot be built (e.g. suppliers not loaded). */
  fallbackOptions?: DeliverySupplierOption[];
};

/**
 * Builds destination-supplier Select options as the split supplier's family:
 * parent + all siblings that share the same parentSupplierId.
 * Sorted: split supplier → associated → rest.
 */
export function buildFamilySupplierOptionsForDelivery(
  params: BuildFamilySupplierOptionsParams,
): DeliverySupplierOption[] {
  const fallback = params.fallbackOptions || [];
  const splitSupplierId = getStrId(params.splitSupplierId);
  if (!splitSupplierId) {
    return fallback;
  }

  const allSuppliers = params.allSuppliers;
  if (!allSuppliers?.length) {
    return fallback;
  }

  const associateIds = new Set(
    Array.from(params.associateIds || []).map((id) =>
      String(id).trim().toLowerCase(),
    ),
  );
  const familyRootId = resolveSupplierFamilyRootId({
    splitSupplierId,
    orderData: params.orderData,
    allSuppliers,
  });
  const familyRootLower = familyRootId.toLowerCase();
  const splitLower = splitSupplierId.toLowerCase();

  const familyOptions: DeliverySupplierOption[] = [];
  const seen = new Set<string>();

  for (const supplier of allSuppliers) {
    const id = getStrId(supplier?._id);
    if (!id) continue;

    const parentId = getStrId(supplier?.parentSupplierId);
    const isFamilyMember =
      id.toLowerCase() === familyRootLower ||
      (parentId.length > 0 && parentId.toLowerCase() === familyRootLower) ||
      id.toLowerCase() === splitLower;

    if (!isFamilyMember) continue;

    const option = toDeliveryOption(supplier, associateIds);
    if (!option) continue;

    const key = option.supplierId.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    familyOptions.push(option);
  }

  if (
    splitSupplierId &&
    !familyOptions.some((opt) => opt.supplierId.toLowerCase() === splitLower)
  ) {
    familyOptions.unshift({
      supplierId: splitSupplierId,
      businessName: params.splitSupplierName || "Selected Supplier",
      branchName: null,
      branchCode: null,
      isAssociated: associateIds.has(splitLower),
    });
  }

  if (familyOptions.length === 0) {
    return fallback;
  }

  const splitOption =
    familyOptions.find((opt) => opt.supplierId.toLowerCase() === splitLower) ??
    null;
  const associated = familyOptions.filter(
    (opt) =>
      opt.isAssociated && opt.supplierId.toLowerCase() !== splitLower,
  );
  const rest = familyOptions.filter(
    (opt) =>
      opt.supplierId.toLowerCase() !== splitLower && !opt.isAssociated,
  );

  return [...(splitOption ? [splitOption] : []), ...associated, ...rest];
}
